import buildOrgTree from './orgTree';

/**
 * buildOrgReport - pure derivation of the "Organization Structure" export
 * document's content model from the same flat `nodes` array `OrgChart.js`
 * renders. No DOM, no fetch - fully unit-testable.
 *
 * Reuses `buildOrgTree` (not a re-implementation) for the forest/children
 * shape and its (first_name, last_name, id) ordering.
 *
 * Root selection and orphans: `buildOrgTree` already separates genuinely
 * disconnected rows (no manager AND no reports of their own - e.g. leftover
 * test fixtures) into `orphans`, apart from `roots`. When the payload's
 * `roots` has more than one entry (a manager_id cycle, or multiple real
 * disconnected org units), `root` is the one with the LARGEST subtree (ties
 * broken by name), not simply the first by name - a same-sized second org
 * unit should not get to outrank the real CEO for the "root" slot just
 * because its name sorts first. Every other root - which, by definition,
 * has descendants of its own - still becomes a section. Every `orphan` is
 * pure noise (a disconnected single employee, not an org unit) - it still
 * counts in `stats.headcount` and `stats.unassigned`, but it is never a
 * section and never appears in the mini chart.
 */

// (first_name, last_name, id) - the tie-break for every headcount-ordered
// list (root selection, sections, and a section's named sub-manager rows).
// Only ever called on raw tree nodes (the objects `buildOrgTree` produces,
// with `.children`/`.first_name` etc.) - never on a section/row object,
// which has none of those fields and would silently compare as "equal"
// (NaN from `undefined - undefined` sorts as a no-op, not a crash) - see
// the section sort below for why it matters to sort BEFORE building
// sections, not after.
const compareByName = (a, b) => {
  if (a.first_name !== b.first_name) return a.first_name < b.first_name ? -1 : 1;
  if (a.last_name !== b.last_name) return a.last_name < b.last_name ? -1 : 1;
  return a.id - b.id;
};

// A node's own headcount plus every descendant's - the "N" behind every
// "Total Headcount: N" / "Reporting line: N" line and the sort key for root
// selection, sections, and a section's named sub-manager rows. Memoised per
// node (a WeakMap, keyed by the node object itself) so a headcount-ordered
// sort over many siblings does O(n) work instead of recomputing each node's
// whole subtree on every comparison - safe across calls because
// `buildOrgTree` hands back a fresh set of node objects each time, so there
// is nothing stale for a later call to hit.
const headcountCache = new WeakMap();
const subtreeHeadcount = (node) => {
  if (headcountCache.has(node)) return headcountCache.get(node);
  const count = 1 + (node.children || []).reduce((sum, child) => sum + subtreeHeadcount(child), 0);
  headcountCache.set(node, count);
  return count;
};

const byHeadcountThenName = (a, b) => {
  const diff = subtreeHeadcount(b) - subtreeHeadcount(a);
  return diff !== 0 ? diff : compareByName(a, b);
};

// Every position held by `node`'s DESCENDANTS - never `node` itself. Used
// only by `roleCountsFor` below, which in turn is now only ever called on a
// genuinely childless section LEADER (the `ownRoleCounts` fallback, since a
// leader's named sub-managers and leaf reports are rolled up a different
// way - see `buildSection`) - a leaf leader falls back to its own position
// x1, so that section's table is never empty even with nothing to roll up.
const collectDescendantPositions = (node, acc) => {
  (node.children || []).forEach((child) => {
    acc.push(child.position);
    collectDescendantPositions(child, acc);
  });
  return acc;
};

const roleCountsFor = (node) => {
  const positions = collectDescendantPositions(node, []);
  if (positions.length === 0) {
    return [{ position: node.position, count: 1 }];
  }

  const counts = new Map();
  positions.forEach((position) => {
    counts.set(position, (counts.get(position) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([position, count]) => ({ position, count }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
    });
};

// aggregateByDesignation - pure aggregation of a flat list of report NODES
// by their own `position` into "Designation xN" counts. Depth-agnostic: the
// CALLER decides how deep the list goes - the export mini chart and a
// section's leaf-IC role rows (`buildSection` below) pass a leader's direct
// reports, one level, while a section's named sub-manager row passes that
// manager's WHOLE flattened subtree (`collectDescendantNodes`, any depth)
// so the role chain reflects every report beneath them, not just their
// direct ones. A missing/empty position buckets under "Unspecified role"
// rather than being dropped, so a report record with no title on file
// still gets counted. Sort order: count desc, then position asc on a tie.
export const aggregateByDesignation = (nodes) => {
  const list = nodes || [];
  const counts = new Map();
  list.forEach((node) => {
    const position = node.position || 'Unspecified role';
    counts.set(position, (counts.get(position) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([position, count]) => ({ position, count }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
    });
};

// Every DESCENDANT of `node` (not `node` itself) as a full node object, at
// ANY depth - not just direct children. Feeds a section's named
// sub-manager rows below: `aggregateByDesignation(collectDescendantNodes(
// manager, []))` rolls up everyone beneath that manager, however many
// levels deep, into one "Designation xN" chain - the same depth-first
// flattening `roleCountsFor` already does via `collectDescendantPositions`,
// just handing back node objects instead of bare position strings so the
// caller can compose it with the shared `aggregateByDesignation` primitive
// instead of a second counting implementation.
const collectDescendantNodes = (node, acc) => {
  (node.children || []).forEach((child) => {
    acc.push(child);
    collectDescendantNodes(child, acc);
  });
  return acc;
};

// One leader -> one section. `leader` may be the root's own direct report,
// or (multi-root case) another top-level root with descendants of its own.
//
// Section-table rows match the reference document's style: built from the
// leader's DIRECT reports only (never the leader's own title - that is
// already named in the section header), split in two -
//   - a direct report WITH descendants of their own is a named sub-manager:
//     one row, their name + title on the left (the same style as the
//     section's own leader header), their WHOLE subtree - not just their
//     direct reports - rolled up into a "Designation xN" chain on the
//     right via `aggregateByDesignation(collectDescendantNodes(manager))`.
//     Their own title never appears in that chain; it is already named on
//     the left, so repeating it there would double-count it.
//   - a direct report who is a LEAF (no reports of their own) is never
//     named individually - every leaf direct report is grouped by
//     designation into anonymous role rows instead, one row per distinct
//     title, so seven Software Engineers read as one "Software Engineer
//     x7" row rather than seven blank or seven repeated rows.
// Named sub-manager rows come first (subtree headcount desc, then name -
// `byHeadcountThenName`, the same rule sections themselves are ordered by),
// then the leaf role rows (count desc, then position asc -
// `aggregateByDesignation`'s own order).
const buildSection = (leader) => {
  const directReports = leader.children || [];
  const subManagers = directReports.filter((report) => (report.children || []).length > 0);
  const leafReports = directReports.filter((report) => (report.children || []).length === 0);

  const managerRows = [...subManagers]
    .sort(byHeadcountThenName)
    .map((manager) => ({
      manager,
      roles: aggregateByDesignation(collectDescendantNodes(manager, [])),
    }));

  const roleRows = aggregateByDesignation(leafReports);

  const section = {
    leader,
    headcount: subtreeHeadcount(leader),
    managerRows,
    roleRows,
  };

  // A true leaf leader (no direct reports at all, so no manager rows and
  // no role rows either) gets the same "own position x1" fallback
  // `roleCountsFor` already gave before this rework - reusing it here
  // rather than re-deriving `[{ position: leader.position, count: 1 }]` a
  // second way keeps the "no descendants" rule in exactly one place.
  if (directReports.length === 0) {
    section.ownRoleCounts = roleCountsFor(leader);
  }

  return section;
};

const emptyReport = (headcount = 0) => ({
  generatedAt: new Date().toISOString(),
  stats: { headcount, departments: 0, largestDepartment: null, rootDirectReports: 0, unassigned: 0 },
  root: null,
  sections: [],
});

const buildOrgReport = (nodes) => {
  const flat = nodes || [];
  // `buildOrgTree` already splits top-level nodes with zero descendants
  // (unlinked single employee rows) into `orphans`, separate from `roots` -
  // so `roots` here only ever contains genuine, if possibly disconnected,
  // org units. Each orphan is noise, not a section, but is still a real
  // active employee for the headcount stat.
  const { roots, orphans } = buildOrgTree(flat);

  if (roots.length === 0) {
    // No true root with any descendants at all (e.g. every node's manager_id
    // also appears in the payload, forming a pure cycle, or every node is
    // an orphan) - still real, active employees, so the headcount stat
    // reflects them even though nothing else is derivable.
    return emptyReport(flat.length);
  }

  // R1: largest subtree wins the `root` slot, ties broken by name - not
  // simply "first root by name", which would let a same-named disconnected
  // single row (zero descendants) outrank the actual CEO.
  const orderedRoots = [...roots].sort(byHeadcountThenName);
  const [root, ...extraRootsWithDescendants] = orderedRoots;

  // R2: every remaining root already has descendants of its own (that's
  // what keeps it out of `orphans`), so it still gets a section. The
  // orphans are excluded from both the sections and the chart.
  const unassigned = orphans.length;

  // Departments are grouped by department_id (the stable key); the display
  // name is carried along for the "Largest Dept (Name)" stat tile. Ties
  // break alphabetically for a deterministic tile regardless of insertion
  // order in the API payload.
  const departmentGroups = new Map();
  flat.forEach((node) => {
    const key = node.department_id != null ? `id:${node.department_id}` : `name:${node.department_name}`;
    const existing = departmentGroups.get(key) || { name: node.department_name, count: 0 };
    existing.count += 1;
    departmentGroups.set(key, existing);
  });

  let largestDepartment = null;
  departmentGroups.forEach((dept) => {
    if (
      !largestDepartment
      || dept.count > largestDepartment.count
      || (dept.count === largestDepartment.count && (dept.name || '') < (largestDepartment.name || ''))
    ) {
      largestDepartment = { name: dept.name, count: dept.count };
    }
  });

  const stats = {
    headcount: flat.length,
    departments: departmentGroups.size,
    largestDepartment,
    rootDirectReports: root.children.length,
    unassigned,
  };

  // Direct reports of the root form the ordinary sections; any other root
  // that has descendants of its own becomes a section too, mixed into the
  // same headcount-ordered list rather than a separate one. The nodes are
  // sorted BEFORE `buildSection` runs, not after - `byHeadcountThenName`
  // reads `.children`/`.first_name` off a raw tree node, and a section
  // object has neither, so sorting section objects with it is a silent
  // no-op (see the comment on `compareByName`).
  const sections = [...root.children, ...extraRootsWithDescendants]
    .sort(byHeadcountThenName)
    .map(buildSection);

  return {
    generatedAt: new Date().toISOString(),
    stats,
    root,
    sections,
  };
};

export default buildOrgReport;
